import React from 'react'
import {
  Select,
  SelectValue,
  Button,
  Popover,
  ListBox,
  ListBoxItem,
} from 'react-aria-components'
import { FontWeight, FontSize } from '../../types'
import {
  GOOGLE_FONTS,
  GoogleFont,
  FONT_WEIGHT_LABELS,
  FONT_SIZE_LABELS,
  DISPLAY_FONT_SIZES,
  BODY_FONT_SIZES,
  isWeightAvailable,
  getFontFamilyValue,
} from '../../data/googleFonts'

interface FontPickerProps {
  label: string
  family: string
  weight?: FontWeight
  size?: FontSize
  onFamilyChange: (family: string) => void
  onWeightChange?: (weight: FontWeight) => void
  onSizeChange?: (size: FontSize) => void
  showWeightPicker?: boolean
  showSizePicker?: boolean
  fontFilter?: (font: GoogleFont) => boolean
  sizeOptions?: FontSize[]
}

export function FontPicker({
  label,
  family,
  weight = 'normal',
  size = 'base',
  onFamilyChange,
  onWeightChange,
  onSizeChange,
  showWeightPicker = true,
  showSizePicker = true,
  fontFilter,
  sizeOptions,
}: FontPickerProps) {
  const filteredFonts = fontFilter ? GOOGLE_FONTS.filter(fontFilter) : GOOGLE_FONTS

  // Get available weights for the selected font
  const selectedFont = GOOGLE_FONTS.find((f) => f.family === family)
  const availableWeights = selectedFont?.weights ?? ['normal', 'bold']

  // Default size options based on context
  const availableSizes = sizeOptions ?? BODY_FONT_SIZES

  return (
    <div className="space-y-2">
      <label className="text-xs font-medium text-gray-600">{label}</label>

      <div className="space-y-2">
        {/* Font Family Select */}
        <Select
          selectedKey={family}
          onSelectionChange={(key) => {
            const newFamily = key as string
            onFamilyChange(newFamily)
            // If current weight is not available, reset to first available
            if (onWeightChange && !isWeightAvailable(newFamily, weight)) {
              const newFont = GOOGLE_FONTS.find((f) => f.family === newFamily)
              if (newFont && newFont.weights.length > 0) {
                onWeightChange(newFont.weights[0])
              }
            }
          }}
        >
          <Button className="w-full flex items-center justify-between px-3 py-2 text-sm border border-gray-200 rounded-md bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent text-left cursor-pointer">
            <SelectValue className="truncate">
              {({ selectedText }) => (
                <span style={{ fontFamily: getFontFamilyValue(family) }}>
                  {selectedText || family}
                </span>
              )}
            </SelectValue>
            <svg className="w-4 h-4 text-gray-500 flex-shrink-0 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </Button>
          <Popover className="w-[--trigger-width] bg-white border border-gray-200 rounded-md shadow-lg overflow-hidden">
            <ListBox className="max-h-60 overflow-auto outline-none p-1">
              {filteredFonts.map((font) => (
                <ListBoxItem
                  key={font.family}
                  id={font.family}
                  className="px-3 py-2 text-sm cursor-pointer rounded-md outline-none data-[focused]:bg-gray-100 data-[selected]:bg-gray-900 data-[selected]:text-white cursor-pointer"
                  style={{ fontFamily: getFontFamilyValue(font.family) }}
                >
                  {font.family}
                </ListBoxItem>
              ))}
            </ListBox>
          </Popover>
        </Select>

        {/* Weight & Size Row */}
        <div className="grid grid-cols-2 gap-2">
          {/* Weight Select */}
          {showWeightPicker && onWeightChange && (
            <Select
              selectedKey={weight}
              onSelectionChange={(key) => onWeightChange(key as FontWeight)}
            >
              <Button className="w-full flex items-center justify-between px-3 py-2 text-sm border border-gray-200 rounded-md bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent text-left cursor-pointer">
                <SelectValue className="truncate">
                  {() => FONT_WEIGHT_LABELS[weight]}
                </SelectValue>
                <svg className="w-4 h-4 text-gray-500 flex-shrink-0 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </Button>
              <Popover className="w-[--trigger-width] bg-white border border-gray-200 rounded-md shadow-lg overflow-hidden">
                <ListBox className="max-h-60 overflow-auto outline-none p-1">
                  {availableWeights.map((w) => (
                    <ListBoxItem
                      key={w}
                      id={w}
                      className="px-3 py-2 text-sm cursor-pointer rounded-md outline-none data-[focused]:bg-gray-100 data-[selected]:bg-gray-900 data-[selected]:text-white cursor-pointer"
                    >
                      {FONT_WEIGHT_LABELS[w]}
                    </ListBoxItem>
                  ))}
                </ListBox>
              </Popover>
            </Select>
          )}

          {/* Size Select */}
          {showSizePicker && onSizeChange && (
            <Select
              selectedKey={size}
              onSelectionChange={(key) => onSizeChange(key as FontSize)}
            >
              <Button className="w-full flex items-center justify-between px-3 py-2 text-sm border border-gray-200 rounded-md bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent text-left cursor-pointer">
                <SelectValue className="truncate">
                  {() => FONT_SIZE_LABELS[size]}
                </SelectValue>
                <svg className="w-4 h-4 text-gray-500 flex-shrink-0 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </Button>
              <Popover className="w-[--trigger-width] bg-white border border-gray-200 rounded-md shadow-lg overflow-hidden">
                <ListBox className="max-h-60 overflow-auto outline-none p-1">
                  {availableSizes.map((s) => (
                    <ListBoxItem
                      key={s}
                      id={s}
                      className="px-3 py-2 text-sm cursor-pointer rounded-md outline-none data-[focused]:bg-gray-100 data-[selected]:bg-gray-900 data-[selected]:text-white cursor-pointer"
                    >
                      {FONT_SIZE_LABELS[s]}
                    </ListBoxItem>
                  ))}
                </ListBox>
              </Popover>
            </Select>
          )}
        </div>
      </div>
    </div>
  )
}

// Simplified mono font picker (no weight or size)
interface MonoFontPickerProps {
  label: string
  family: string
  onFamilyChange: (family: string) => void
}

export function MonoFontPicker({ label, family, onFamilyChange }: MonoFontPickerProps) {
  const monoFonts = GOOGLE_FONTS.filter((f) => f.category === 'monospace')

  return (
    <div className="space-y-2">
      <label className="text-xs font-medium text-gray-600">{label}</label>

      <Select selectedKey={family} onSelectionChange={(key) => onFamilyChange(key as string)}>
        <Button className="w-full flex items-center justify-between px-3 py-2 text-sm border border-gray-200 rounded-md bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent text-left cursor-pointer">
          <SelectValue className="truncate font-mono">
            {({ selectedText }) => selectedText || family}
          </SelectValue>
          <svg className="w-4 h-4 text-gray-500 flex-shrink-0 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </Button>
        <Popover className="w-[--trigger-width] bg-white border border-gray-200 rounded-md shadow-lg overflow-hidden">
          <ListBox className="max-h-60 overflow-auto outline-none p-1">
            {monoFonts.map((font) => (
              <ListBoxItem
                key={font.family}
                id={font.family}
                className="px-3 py-2 text-sm cursor-pointer rounded-md outline-none font-mono data-[focused]:bg-gray-100 data-[selected]:bg-gray-900 data-[selected]:text-white"
              >
                {font.family}
              </ListBoxItem>
            ))}
          </ListBox>
        </Popover>
      </Select>
    </div>
  )
}
